import React, { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiAward, FiStar, FiCalendar, FiClock, FiCheckCircle, FiHeart,
  FiArrowRight, FiPlay, FiBookOpen, FiCompass, FiShield, FiZap,
  FiUsers, FiGlobe, FiMapPin, FiX, FiCheck, FiShare2,
  FiChevronRight, FiLayers, FiExternalLink, FiFeather, FiSun
} from "react-icons/fi";
import {
  FaOm, FaQuoteLeft, FaQuoteRight, FaInstagram, FaLinkedinIn, FaYoutube, FaFacebookF,
  FaMedal, FaCertificate, FaCrown, FaGraduationCap, FaLeaf, FaSpa, FaBrain, FaGlobeAsia, FaAward
} from "react-icons/fa";
import { GiLotus, GiMeditation, GiSparkles, GiYinYang, GiChakram } from "react-icons/gi";

// ─── MASTER TRAINER PROFILES ──────────────────────────────────────────────────
const LEAD_MASTER = {
  id: "divya-bhojwani",
  name: "Mrs. Divya Nithin Bhojwani",
  role: "Founder, International Gold Medalist & Master Acharya",
  tagline: "Bridging Himalayan Lineage with Modern Biomechanics",
  experience: "15+ Years Mastery",
  studentsCount: "10,000+",
  image: "/assets/Divya_Bhojwani.png",
  secondaryImage: "/assets/Divya_1.png",
  accentColor: "#F29A0C",
  mantra: "Yoga is not about touching your toes, it is what you learn on the way down. It is the sacred alchemy of uniting body, breath, and pure consciousness.",
  bio: "Celebrated globally as one of the living masters of classical Yogic sciences, Mrs. Divya Nithin Bhojwani has represented India on prestigious world stages, clinching international Gold honors. Over a decade and a half, she has pioneered holistic yoga programs that harmonize ancient Vedic purity with contemporary posture alignment, nurturing more than 10,000 students across 15+ countries.",
  credentials: [
    { label: "International Gold Medalist", desc: "World Yoga Championship & National Accolades", icon: FaMedal },
    { label: "Grand Master E-RYT 500", desc: "Yoga Alliance USA Registered Master Teacher", icon: FaCertificate },
    { label: "Ayush Ministry Certified", desc: "Govt. of India Yoga Protocol Lead Examiner", icon: FiShield },
    { label: "Federation Judge", desc: "Yoga Sports Championship Accredited Juror", icon: FaCrown }
  ],
  specialties: [
    "Classical Hatha & Ashtanga Vinyasa Flow",
    "Therapeutic Yogic Alignment & Spinal Rehabilitation",
    "Pranayama & Kundalini Energy Awakening",
    "Advanced Meditation & Sacred Yoga Nidra",
    "Holistic Sattvic Nutrition & Lifestyle",
    "Master Teacher Training (TTC 200/300/500 Hr)"
  ],
  awards: [
    { year: "2026", title: "National Yogaratna Award", org: "Yoga Confederation of India" },
    { year: "2025", title: "International Gold Medalist", org: "World Yoga Championship" },
    { year: "2024", title: "Distinguished Acharya Honor", org: "Ministry of Ayush" },
    { year: "2023", title: "Global Holistic Wellness Leader", org: "International Yoga Summit" }
  ]
};

const TRAINERS_LIST = [
  {
    id: "sapanpreet-grewal",
    name: "Sapanpreet Grewal",
    role: "Yog & Meditation Instructor",
    title: "Hatha Yog & Mindfulness Guide (Yoga Siromani)",
    category: "philosophy",
    categoryLabel: "Philosophy & Meditation",
    experience: "10+ Yrs Corporate & Spiritual Sadhana",
    image: "/assets/Sapanpreet_Grewal.png",
    accent: "#6B8A5A",
    sealText: "Sivananda Kutir · Uttarkashi",
    rating: "4.98",
    students: "4,100+",
    tags: ["Hatha Yog", "Meditation", "Emotional Healing", "Mindfulness", "Corporate Transition"],
    bio: "A Hatha yog instructor sharing her transformative journey from over a decade in aviation & luxury hospitality (Andaz Delhi by Hyatt, Leela Palace Udaipur) to a grounded spiritual foundation, guiding students into conscious living.",
    philosophy: "My teaching philosophy centers on using yoga as a tool for mental well-being, self-growth, and introspection. Exploring the inner self through mindful movement, breathwork, and deep meditation.",
    quote: "Happiness isn’t something we find; it’s something we nurture within ourselves.",
    careerJourney: "Over a decade managing cabins and operations in aviation, later Guest Experience Manager at luxury hotels. Her spiritual journey revealed deep karmic wisdom, surrender, kindness, and self-awareness.",
    certifications: [
      "Yoga Siromani (200 Hours) - Sivananda Yoga Vedanta Ashrams, Sivananda Kutir Uttarkashi",
      "Advance Yogasana Teacher Training Course - Bilvayogashala (ISO 9001:2015)",
      "Wellness Coach at Livgro Vision Foundation",
      "Community Development Partner with Goonj & Nirmal Chaya NGOs"
    ],
    offerings: [
      { title: "Classical Hatha Yog", desc: "Sivananda-lineage postures balancing solar & lunar energies." },
      { title: "Conscious Living & Healing", desc: "Guiding emotional release and mental stillness through yogic philosophy." },
      { title: "Corporate Stress Transition", desc: "Mentoring busy professionals into grounded emotional resilience." },
      { title: "Breath & Mindfulness", desc: "Cultivating serenity, self-awareness, and restorative stillness." }
    ],
    location: "New Delhi, India"
  },
  {
    id: "varsha-rochwani",
    name: "Varsha Rochwani",
    role: "Certified Yoga Instructor · Women's Wellness Specialist",
    title: "Face Yoga & Chair Yoga Specialist (Ayush Ministry IIAS Diploma)",
    category: "therapy",
    categoryLabel: "Therapy & Women's Health",
    experience: "6+ Years Teaching Mastery",
    image: "/assets/Varsha_Rochwani.png",
    accent: "#D97706",
    sealText: "IIAS Ayush · First Division",
    rating: "4.99",
    students: "3,800+",
    tags: ["Face Yoga", "Chair Yoga", "Women's Wellness", "PCOD / Fertility", "Therapeutic Yoga"],
    bio: "Certified yoga instructor with 6 years of teaching experience specializing in Face Yoga, Chair Yoga, and Women's Hormonal Health. Passionate about making wellness accessible, enjoyable, and deeply restorative for every age group.",
    philosophy: "I believe yoga is for everyone, regardless of age, flexibility, or experience. Teaching came naturally to me through personal healing, helping students move with confidence, improve well-being, and reconnect with inner grace.",
    quote: "Teaching yoga to every age with specialization in Face Yoga and Chair Yoga, making natural wellness accessible and joyful.",
    certifications: [
      "Diploma in Women's Wellness (First Division) - IIAS School of Yoga (Ayush Ministry Govt. of India)",
      "Face Yoga Teacher Training Course - Soham Dept of Yoga & Naturopathy, KCES (International Yoga Org)",
      "Therapeutic Yoga Teacher Training Course - Yog Namaste Academy (ISO 9001:2015)",
      "300 Hours Ashtanga Yoga & Practice - Patanjali Yogpeeth Trust",
      "Advanced Pranayama & Meditation - IIAS School of Yoga, Noida"
    ],
    offerings: [
      { title: "Face Yoga & Natural Aesthetics", desc: "Facial muscle toning and lymphatic drainage for natural vitality." },
      { title: "Chair Yoga for Seniors & Accessibility", desc: "Gentle restorative movement tailored for seniors and limited mobility." },
      { title: "Women's Hormonal Health Therapy", desc: "Clinical yoga therapy for PCOD, fertility, pregnancy, postpartum & menopause." },
      { title: "Therapeutic Asana & Breath", desc: "Restorative breathwork to dissolve tension, stress, and chronic stiffness." }
    ],
    location: "Maharashtra, India"
  },
  {
    id: "hithesh-gowda",
    name: "Hithesh Gowda C A",
    role: "Yoga Instructor & IB Creative Educator",
    title: "Holistic Yoga & Youth Mindfulness Specialist (S-VYASA Certified)",
    category: "asana",
    categoryLabel: "Asana & Vinyasa",
    experience: "S-VYASA Certified Instructor",
    image: "/assets/Hitesh.png",
    accent: "#2D6A4F",
    sealText: "S-VYASA · Bengaluru",
    rating: "4.97",
    students: "2,900+",
    tags: ["Hatha Yoga", "Vinyasa Flow", "Youth Mindfulness", "Visual Arts", "IB Pedagogy"],
    bio: "Dedicated Yoga Instructor and IB Visual Arts Educator with a strong foundation in Hatha and Vinyasa Yoga from S-VYASA Yoga University. Blends mindful movement with creative mindfulness, fostering self-expression, mental clarity, and physical strength.",
    philosophy: "Cultivating body-breath awareness as an art form. Movement is the canvas, and breath is the brush through which we create lifelong inner stillness and physical vitality.",
    quote: "Balancing inner peace with vibrant strength through mindfulness and creative expression.",
    certifications: [
      "Yoga Instructor Course (YIC) - S-VYASA Deemed to be University, Bengaluru",
      "Teacher Training Certificate in Visual Arts (IB DP/MYP)",
      "Certificate of Excellence in Yoga Practice & Instruction"
    ],
    offerings: [
      { title: "Hatha & Dynamic Vinyasa", desc: "Structured alignment, strength development, and graceful sequence flow." },
      { title: "Youth & Creative Mindfulness", desc: "Specialized focus and stress relief sessions for students and young professionals." },
      { title: "Breathing & Relaxation", desc: "Pranayama and deep yogic relaxation for holistic vitality." }
    ],
    location: "Bengaluru / Hubballi, Karnataka"
  }
];

const PEDAGOGY_PILLARS = [
  {
    num: "01",
    title: "Authentic Lineage Transmission",
    desc: "Every technique is rooted in centuries-old classical traditions (Patanjali Ashtanga, Sivananda, Hatha Pradipika) rather than modern diluted fitness trends.",
    icon: FaOm,
    glow: "rgba(242,154,12,0.4)"
  },
  {
    num: "02",
    title: "Anatomical Precision & Safety",
    desc: "Biomechanical cueing, intelligent sequencing, and therapeutic modifications ensure complete joint integrity and zero injury risk for every age.",
    icon: GiChakram,
    glow: "rgba(255,180,0,0.4)"
  },
  {
    num: "03",
    title: "Pranic & Mental Alchemy",
    desc: "Beyond mere physical flexibility, our masters guide students through the subtle layers (Koshas) of breathwork, mudras, bandhas, and dhyana.",
    icon: GiLotus,
    glow: "rgba(157,192,139,0.4)"
  },
  {
    num: "04",
    title: "Personalized Transformation",
    desc: "Recognizing that every body, dosha, and spiritual path is unique, our acharyas provide compassionate, tailored guidance for lifelong empowerment.",
    icon: FaLeaf,
    glow: "rgba(242,154,12,0.4)"
  }
];

const LINEAGES = [
  {
    name: "Yoga Alliance USA",
    badge: "E-RYT 500 Certified",
    subtitle: "Internationally Recognized Worldwide Credential",
    icon: FaCertificate
  },
  {
    name: "Ministry of Ayush",
    badge: "Govt. of India Certified",
    subtitle: "National Yoga Protocol & Quality Standards",
    icon: FiShield
  },
  {
    name: "Sivananda Vedanta",
    badge: "Yoga Siromani Lineage",
    subtitle: "Classical Himalayas Ashrams Tradition",
    icon: FaOm
  },
  {
    name: "S-VYASA University",
    badge: "Research & Academic Accreditations",
    subtitle: "Scientific Evidence-Based Yoga Research",
    icon: FaGraduationCap
  },
  {
    name: "IIAS Indian Institute",
    badge: "Ayush Ministry Diploma",
    subtitle: "Advanced Women's Wellness & Therapy",
    icon: FaCrown
  }
];

// ─── 3D TILT CARD COMPONENT ──────────────────────────────────────────────────
function LuxuryTiltCard({ children, className = "", maxTilt = 7 }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseX = useSpring(x, { stiffness: 320, damping: 24 });
  const mouseY = useSpring(y, { stiffness: 320, damping: 24 });

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    x.set((clientX - left) / width - 0.5);
    y.set((clientY - top) / height - 0.5);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  const rotateX = useTransform(mouseY, [-0.5, 0.5], [maxTilt, -maxTilt]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], [-maxTilt, maxTilt]);

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`perspective-1000 ${className}`}
    >
      {children}
    </motion.div>
  );
}

// ─── MAIN TRAINERS PAGE COMPONENT ────────────────────────────────────────────
export default function Trainers() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeTrainerModal, setActiveTrainerModal] = useState(null);
  const [activeMasterTab, setActiveMasterTab] = useState("credentials");

  const heroSectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroSectionRef,
    offset: ["start start", "end start"],
  });

  const heroParallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.2]);

  // Interactive mouse light tracking on the bottom card
  const bottomCardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const handleBottomCardMouseMove = (e) => {
    if (!bottomCardRef.current) return;
    const rect = bottomCardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const categories = [
    { id: "all", label: "All Faculty Masters", count: TRAINERS_LIST.length },
    { id: "asana", label: "Asana & Vinyasa", count: TRAINERS_LIST.filter(t => t.category === "asana").length },
    { id: "philosophy", label: "Philosophy & Meditation", count: TRAINERS_LIST.filter(t => t.category === "philosophy").length },
    { id: "therapy", label: "Therapy & Women's Health", count: TRAINERS_LIST.filter(t => t.category === "therapy").length },
  ];

  const filteredTrainers = useMemo(() => {
    if (selectedCategory === "all") return TRAINERS_LIST;
    return TRAINERS_LIST.filter(t => t.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <div className="min-h-screen bg-[#FFF8EE] text-[#111111] overflow-x-hidden selection:bg-[#F29A0C]/20 selection:text-[#F29A0C] relative">
      
      {/* ══════════════════════════════════════════════════════════════════════════
          1. LUMINOUS HERO SECTION (RISHIKESH SUNRISE ASHRAM + RADIANT SAFFRON OVERLAYS)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section ref={heroSectionRef} className="relative min-h-[96vh] flex items-center justify-center pt-36 pb-28 lg:pt-40 lg:pb-36 px-4 sm:px-6 lg:px-12 overflow-hidden">
        
        {/* Full-bleed Background Image with High Definition */}
        <motion.div
          style={{ y: heroParallaxY, backgroundImage: "url('/assets/trainers_hero_bg.jpg')" }}
          className="absolute inset-0 w-full h-full bg-cover bg-center scale-[1.08]"
        />

        {/* Overlapping Rich Amber, Saffron & Warm Espresso Color Tints */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(20, 8, 2, 0.62) 0%, rgba(242, 154, 12, 0.3) 38%, rgba(35, 14, 2, 0.55) 70%, rgba(55, 68, 44, 0.72) 100%)",
          }}
        />

        {/* Ambient Warm Golden Sun Burst & Saffron Glow */}
        <div
          className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[850px] h-[580px] pointer-events-none rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(242,154,12,0.42) 0%, rgba(255,180,0,0.2) 45%, transparent 72%)",
            filter: "blur(60px)",
          }}
        />

        {/* Bottom Olive Green Calming Transition Blend */}
        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(78, 98, 64, 0.4) 45%, rgba(68, 88, 54, 0.85) 80%, #FFF8EE 100%)",
          }}
        />

        {/* Floating Sacred Aura Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(14)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -45, 0],
                opacity: [0.35, 0.9, 0.35],
                scale: [1, 1.4, 1],
              }}
              transition={{
                duration: 4.5 + i * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.3,
              }}
              className="absolute rounded-full bg-[#FFD166]/60 blur-[0.5px]"
              style={{
                width: `${4 + (i % 4) * 2}px`,
                height: `${4 + (i % 4) * 2}px`,
                top: `${12 + (i * 7) % 72}%`,
                left: `${6 + (i * 9) % 88}%`,
              }}
            />
          ))}
        </div>

        {/* Hero Central Content */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 max-w-4xl mx-auto text-center space-y-7 pt-4"
        >
          
          {/* Top Pill Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-black/55 backdrop-blur-3xl border border-white/30 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
          >
            <GiLotus className="text-[#FFD166] text-lg animate-pulse" />
            <span className="text-[12px] font-bold uppercase tracking-[3px] text-white">
              Divyyog Master Faculty & Acharyas
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#F29A0C]" />
            <span className="text-[12px] font-semibold text-[#FFD166]">
              Authentic Vedic Lineage
            </span>
          </motion.div>

          {/* Main Hero Headline with Crystal Clear Contrast */}
          <div className="relative">
            {/* Soft Ambient Contrast Vignette Behind Headline */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[700px] h-[220px] bg-black/50 rounded-full blur-[45px] pointer-events-none" />

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.2] sm:leading-[1.18] md:leading-[1.15] tracking-[0.02em] sm:tracking-[0.025em]"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <span
                className="block text-white"
                style={{
                  textShadow: "0 4px 20px rgba(0,0,0,0.9), 0 2px 8px rgba(0,0,0,0.85)"
                }}
              >
                The Living Masters of
              </span>
              <span
                className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#FFF2B2] via-[#FFD166] to-[#FFA726] bg-clip-text text-transparent font-black"
                style={{
                  filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.95)) drop-shadow(0 0 30px rgba(242,154,12,0.65))"
                }}
              >
                Yogic Science & Healing
              </span>
            </motion.h1>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-base sm:text-lg md:text-xl text-white/95 font-medium leading-relaxed md:leading-[1.75] tracking-[0.015em] max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
          >
            Learn directly from internationally acclaimed Gold Medalists, Sivananda Acharyas, Ayush Ministry lead examiners, and certified specialists dedicated to your authentic transformation.
          </motion.p>

          {/* Impact Stats / Highlights Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="pt-2 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
          >
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-black/55 backdrop-blur-xl border border-white/25 text-white shadow-lg">
              <FaMedal className="text-[#FFD166] text-base" />
              <span className="text-xs sm:text-sm font-bold">World Gold Medalists</span>
            </div>
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-black/55 backdrop-blur-xl border border-white/25 text-white shadow-lg">
              <FaCertificate className="text-[#9DC08B] text-base" />
              <span className="text-xs sm:text-sm font-bold">Yoga Alliance USA (E-RYT 500)</span>
            </div>
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-black/55 backdrop-blur-xl border border-white/25 text-white shadow-lg">
              <FiUsers className="text-[#FFD166] text-base" />
              <span className="text-xs sm:text-sm font-bold">10,000+ Practitioners</span>
            </div>
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-black/55 backdrop-blur-xl border border-white/25 text-white shadow-lg">
              <FiGlobe className="text-[#F29A0C] text-base" />
              <span className="text-xs sm:text-sm font-bold">15+ Nations</span>
            </div>
          </motion.div>

          {/* Quick Jump Buttons */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="pt-4 flex flex-wrap items-center justify-center gap-4"
          >
            <a
              href="#faculty-directory"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white font-bold text-sm sm:text-base shadow-[0_15px_40px_rgba(242,154,12,0.55)] hover:shadow-[0_20px_50px_rgba(242,154,12,0.7)] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
            >
              <span>Explore Faculty Directory</span>
              <FiArrowRight className="text-lg" />
            </a>

            <a
              href="#lead-master"
              className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-white/95 backdrop-blur-xl border border-white/40 text-[#111111] font-bold text-sm sm:text-base hover:bg-white hover:text-[#F29A0C] transition-all duration-300 shadow-md"
            >
              <span>Meet Founder Acharya</span>
              <FaCrown className="text-[#F29A0C] text-sm" />
            </a>
          </motion.div>

        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          2. LEAD MASTER SPOTLIGHT (Mrs. Divya Nithin Bhojwani)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section id="lead-master" className="py-24 lg:py-32 px-4 sm:px-6 lg:px-12 relative bg-transparent overflow-hidden">
        
        {/* Soft Ambient Radial Glow Behind Master Section */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F29A0C]/8 blur-[120px] pointer-events-none" />

        <div className="max-w-[1440px] mx-auto relative z-10">
          
          {/* Section Heading */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-[#F29A0C] text-xs font-bold uppercase tracking-widest shadow-sm">
              <FaCrown className="text-xs" />
              <span>The Guiding Light</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111111] leading-[1.25] sm:leading-[1.2] tracking-[0.02em] sm:tracking-[0.025em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Meet Our Founder & Master Acharya
            </h2>
            <p className="text-[#666666] text-base sm:text-lg leading-relaxed tracking-[0.01em]">
              Distinguished world champion, accredited examiner, and visionary bringing authentic spiritual yoga to modern lives.
            </p>
          </div>

          {/* Master Card Showcase */}
          <div className="rounded-[40px] border-2 border-[#F4D9B5] bg-white/95 backdrop-blur-3xl p-6 sm:p-10 lg:p-14 shadow-[0_30px_100px_rgba(242,154,12,0.12)] relative overflow-hidden">
            
            {/* Top Glowing Shimmer Border */}
            <div
              className="absolute top-0 left-0 right-0 h-[4px]"
              style={{
                background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #F29A0C, transparent)",
              }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center relative z-10">
              
              {/* Left Column: Portrait & Key Stats */}
              <div className="lg:col-span-5 space-y-6">
                <div className="relative rounded-[32px] overflow-hidden border-2 border-[#F4D9B5] shadow-[0_20px_60px_rgba(0,0,0,0.08)] aspect-[4/5] group bg-[#FFF8EE]">
                  <img
                    src={LEAD_MASTER.image}
                    alt={LEAD_MASTER.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/25" />
                  
                  {/* Top Seal */}
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-xs font-semibold">
                    <FaMedal className="text-[#F29A0C]" />
                    <span>Gold Medalist 2025</span>
                  </div>

                  {/* Bottom Bio Pill */}
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="text-xl font-bold">{LEAD_MASTER.name}</div>
                    <div className="text-xs text-[#F29A0C] font-semibold">{LEAD_MASTER.role}</div>
                  </div>
                </div>

                {/* Quick Master Metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] text-center shadow-sm">
                    <div className="text-xl sm:text-2xl font-bold text-[#F29A0C]">15+</div>
                    <div className="text-[11px] text-[#666666] font-medium">Years Mastery</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] text-center shadow-sm">
                    <div className="text-xl sm:text-2xl font-bold text-[#F29A0C]">10K+</div>
                    <div className="text-[11px] text-[#666666] font-medium">Practitioners</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] text-center shadow-sm">
                    <div className="text-xl sm:text-2xl font-bold text-[#F29A0C]">15+</div>
                    <div className="text-[11px] text-[#666666] font-medium">Nations</div>
                  </div>
                </div>
              </div>

              {/* Right Column: Bio, Sacred Quote & Interactive Tabs */}
              <div className="lg:col-span-7 space-y-6">
                
                {/* Sacred Quote Card */}
                <div className="p-6 sm:p-8 rounded-3xl bg-[#FFF8EE]/90 border border-[#F4D9B5] relative shadow-sm">
                  <FaQuoteLeft className="text-[#F29A0C]/25 text-3xl sm:text-4xl absolute top-6 left-6 -z-0" />
                  <p className="text-base sm:text-lg text-[#222222] font-medium italic relative z-10 leading-[1.75] tracking-[0.015em] pl-2 sm:pl-4">
                    "{LEAD_MASTER.mantra}"
                  </p>
                  <div className="mt-3 text-right text-xs font-bold uppercase tracking-widest text-[#F29A0C]">
                    — Mrs. Divya Nithin Bhojwani
                  </div>
                </div>

                {/* Biography Paragraph */}
                <p className="text-[#555555] text-base sm:text-lg leading-[1.8] tracking-[0.01em] font-normal">
                  {LEAD_MASTER.bio}
                </p>

                {/* Interactive Master Dossier Tabs */}
                <div className="pt-2">
                  <div className="flex flex-wrap gap-2 border-b border-[#F4D9B5] pb-3">
                    <button
                      onClick={() => setActiveMasterTab("credentials")}
                      className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all cursor-pointer ${
                        activeMasterTab === "credentials"
                          ? "bg-[#F29A0C] text-white shadow-md scale-[1.02]"
                          : "bg-[#FFF8EE] text-[#666666] hover:text-[#111111] border border-[#F4D9B5]"
                      }`}
                    >
                      Lineage Accreditations
                    </button>
                    <button
                      onClick={() => setActiveMasterTab("specialties")}
                      className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all cursor-pointer ${
                        activeMasterTab === "specialties"
                          ? "bg-[#F29A0C] text-white shadow-md scale-[1.02]"
                          : "bg-[#FFF8EE] text-[#666666] hover:text-[#111111] border border-[#F4D9B5]"
                      }`}
                    >
                      Master Class Disciplines
                    </button>
                    <button
                      onClick={() => setActiveMasterTab("honors")}
                      className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all cursor-pointer ${
                        activeMasterTab === "honors"
                          ? "bg-[#F29A0C] text-white shadow-md scale-[1.02]"
                          : "bg-[#FFF8EE] text-[#666666] hover:text-[#111111] border border-[#F4D9B5]"
                      }`}
                    >
                      Awards & Recognition
                    </button>
                  </div>

                  {/* Tab Content Display */}
                  <div className="pt-5">
                    {activeMasterTab === "credentials" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        {LEAD_MASTER.credentials.map((cred, i) => {
                          const Icon = cred.icon;
                          return (
                            <div key={i} className="flex items-start gap-3.5 p-4 rounded-2xl bg-[#FFF8EE]/60 border border-[#F4D9B5] shadow-sm">
                              <div className="w-10 h-10 rounded-xl bg-[#F29A0C]/15 text-[#F29A0C] flex items-center justify-center text-lg flex-shrink-0">
                                <Icon />
                              </div>
                              <div>
                                <h4 className="text-sm font-bold text-[#111111]">{cred.label}</h4>
                                <p className="text-xs text-[#666666] leading-tight pt-0.5">{cred.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {activeMasterTab === "specialties" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {LEAD_MASTER.specialties.map((spec, i) => (
                          <div key={i} className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#FFF8EE]/60 border border-[#F4D9B5]">
                            <FiCheckCircle className="text-[#6B8A5A] text-base flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-semibold text-[#333333]">{spec}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeMasterTab === "honors" && (
                      <div className="space-y-2.5">
                        {LEAD_MASTER.awards.map((award, i) => (
                          <div key={i} className="flex items-center justify-between p-3.5 rounded-2xl bg-[#FFF8EE]/60 border border-[#F4D9B5]">
                            <div className="flex items-center gap-3">
                              <FaAward className="text-[#F29A0C] text-lg" />
                              <div>
                                <div className="text-xs sm:text-sm font-bold text-[#111111]">{award.title}</div>
                                <div className="text-xs text-[#666666]">{award.org}</div>
                              </div>
                            </div>
                            <span className="px-3 py-1 rounded-full bg-[#F29A0C]/15 text-[#F29A0C] text-xs font-bold">
                              {award.year}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          3. MASTER FACULTY DIRECTORY & DOSSIERS (CATEGORY FILTERING + MODAL SHOWCASE)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section id="faculty-directory" className="py-24 lg:py-32 px-4 sm:px-6 lg:px-12 relative bg-transparent">
        <div className="max-w-[1440px] mx-auto space-y-12">
          
          {/* Section Header & Category Filter Buttons */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#F4D9B5] pb-8">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 text-[#F29A0C] text-xs font-bold uppercase tracking-widest shadow-sm">
                <FiUsers className="text-xs" />
                <span>Certified Teachers</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111111] leading-[1.25] sm:leading-[1.2] tracking-[0.02em] sm:tracking-[0.025em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Meet the Master Faculty
              </h2>
              <p className="text-[#666666] text-base leading-relaxed tracking-[0.01em]">
                Discover our world-renowned instructors specializing across classical postures, breathwork, and women's wellness.
              </p>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer flex items-center gap-2 ${
                    selectedCategory === cat.id
                      ? "bg-[#F29A0C] text-white shadow-md scale-105"
                      : "bg-white text-[#555555] hover:text-[#111111] border border-[#F4D9B5] hover:bg-[#FFF8EE]"
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    selectedCategory === cat.id ? "bg-black/20 text-white" : "bg-[#FFF8EE] text-[#F29A0C]"
                  }`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Grid of Trainers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            <AnimatePresence mode="popLayout">
              {filteredTrainers.map((trainer) => (
                <motion.div
                  key={trainer.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                >
                  <LuxuryTiltCard className="h-full">
                    <div className="rounded-[36px] bg-white border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_70px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] transition-all duration-500 overflow-hidden flex flex-col justify-between p-6 sm:p-7 relative group h-full">
                      
                      {/* Top Glowing Shimmer on Hover */}
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      <div className="space-y-6">
                        
                        {/* Portrait Container */}
                        <div className="relative rounded-[28px] overflow-hidden aspect-[4/4.5] bg-[#FFF8EE] border border-[#F4D9B5]/80 shadow-inner">
                          <img
                            src={trainer.image}
                            alt={trainer.name}
                            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

                          {/* Top Seal Badge */}
                          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-semibold shadow-md">
                            <GiLotus className="text-[#FFD166]" />
                            <span>{trainer.sealText}</span>
                          </div>

                          {/* Bottom Rating Floating Pill */}
                          <div className="absolute bottom-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-md text-[#111111] text-xs font-bold shadow-md">
                            <FiStar className="text-[#F29A0C] fill-current text-xs" />
                            <span>{trainer.rating}</span>
                          </div>

                          {/* Students Count */}
                          <div className="absolute bottom-4 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-xs font-medium">
                            <FiUsers className="text-[#FFD166] text-xs" />
                            <span>{trainer.students}</span>
                          </div>
                        </div>

                        {/* Trainer Info */}
                        <div className="space-y-2">
                          <h3 className="text-xl sm:text-2xl font-bold text-[#111111] leading-snug tracking-[0.02em] group-hover:text-[#F29A0C] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                            {trainer.name}
                          </h3>
                          <p className="text-xs sm:text-sm font-semibold text-[#F29A0C] leading-[1.5] tracking-[0.015em]">
                            {trainer.title}
                          </p>
                          <p className="text-xs text-[#666666] line-clamp-3 leading-[1.65] tracking-[0.01em] pt-1">
                            {trainer.bio}
                          </p>
                        </div>

                        {/* Skill Tags */}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {trainer.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-2.5 py-1 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[10.5px] font-medium text-[#555555]">
                              {tag}
                            </span>
                          ))}
                          {trainer.tags.length > 3 && (
                            <span className="px-2 py-1 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[10.5px] font-bold text-[#F29A0C]">
                              +{trainer.tags.length - 3} more
                            </span>
                          )}
                        </div>

                      </div>

                      {/* Bottom Action: View Comprehensive Dossier */}
                      <div className="pt-6 mt-6 border-t border-[#F4D9B5]/60">
                        <button
                          onClick={() => setActiveTrainerModal(trainer)}
                          className="w-full py-3.5 px-5 rounded-2xl bg-[#FFF8EE] hover:bg-gradient-to-r hover:from-[#F29A0C] hover:to-[#FF8400] text-[#111111] hover:text-white border border-[#F4D9B5] hover:border-transparent font-bold text-xs sm:text-sm tracking-wide transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-[0_10px_25px_rgba(242,154,12,0.3)] cursor-pointer"
                        >
                          <span>View Full Profile & Credentials</span>
                          <FiChevronRight className="text-base transition-transform group-hover:translate-x-1" />
                        </button>
                      </div>

                    </div>
                  </LuxuryTiltCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          4. THE DIVYYOG TEACHING PEDAGOGY (RICH MULTI-TONE SAFFRON & WARM AMBER GRADIENT + VISIBLE GLOWING OM)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 lg:py-36 px-4 sm:px-6 lg:px-12 relative overflow-hidden text-white"
        style={{
          background: "linear-gradient(180deg, #1A0C04 0%, #351706 20%, #481F08 50%, #2E1205 80%, #150902 100%)",
        }}
      >
        
        {/* Top Radiant Glowing Border Shimmer */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{
            background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, transparent)",
          }}
        />

        {/* Ambient Warm Golden Saffron Sunbursts */}
        <div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(242,154,12,0.28) 0%, rgba(255,132,0,0.12) 45%, transparent 70%)",
            filter: "blur(90px)",
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(255,209,102,0.22) 0%, rgba(242,154,12,0.12) 45%, transparent 70%)",
            filter: "blur(90px)",
          }}
        />

        {/* Large Prominent Sacred Om (ॐ) Centerpiece - Constant & Perfectly Centered */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] sm:w-[750px] sm:h-[750px] flex items-center justify-center pointer-events-none z-0">
          <div className="w-full h-full flex items-center justify-center opacity-30 text-[#FFD166]">
            <FaOm className="w-full h-full drop-shadow-[0_0_80px_rgba(242,154,12,0.85)]" />
          </div>
        </div>

        {/* Floating Sacred Aura Particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(16)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -35, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 4 + i * 0.7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.25,
              }}
              className="absolute rounded-full bg-[#FFD166]/50 blur-[0.5px]"
              style={{
                width: `${3 + (i % 3) * 2}px`,
                height: `${3 + (i % 3) * 2}px`,
                top: `${10 + (i * 8) % 78}%`,
                left: `${5 + (i * 7) % 90}%`,
              }}
            />
          ))}
        </div>

        <div className="max-w-[1440px] mx-auto relative z-10">
          
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#F29A0C]/25 via-[#FF8400]/20 to-[#F29A0C]/25 border border-[#F29A0C]/40 text-[#FFD166] text-xs font-bold uppercase tracking-widest shadow-lg backdrop-blur-md">
              <GiSparkles className="text-sm text-[#FFD166] animate-pulse" />
              <span>Teaching Philosophy</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.25] sm:leading-[1.2] tracking-[0.025em] sm:tracking-[0.03em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
              The Divyyog Standard of <br />
              <span className="bg-gradient-to-r from-[#FFD166] via-[#FFA41B] to-[#FF8400] bg-clip-text text-transparent drop-shadow-[0_4px_25px_rgba(242,154,12,0.6)]">
                Excellence & Mastery
              </span>
            </h2>
            <p className="text-white/85 text-base sm:text-lg max-w-2xl mx-auto font-normal leading-[1.7] tracking-[0.015em]">
              Our pedagogical framework blends ancient Himalayan lineage with modern biomechanical precision to ensure safe, transformative instruction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PEDAGOGY_PILLARS.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={idx}
                  whileHover={{ y: -10, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  className="p-8 rounded-[32px] bg-gradient-to-b from-white/[0.1] to-white/[0.03] border border-white/20 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] hover:border-[#F29A0C] hover:bg-gradient-to-b hover:from-[#F29A0C]/20 hover:to-white/[0.06] hover:shadow-[0_25px_70px_rgba(242,154,12,0.35)] transition-all duration-300 relative space-y-5 overflow-hidden group"
                >
                  {/* Glowing Top Shimmer Accent */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#FFD166] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="flex items-center justify-between relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F29A0C]/30 to-[#FF8400]/10 border border-[#F29A0C]/40 text-[#FFD166] flex items-center justify-center text-2xl shadow-inner group-hover:scale-110 group-hover:bg-[#F29A0C] group-hover:text-white transition-all duration-300">
                      <Icon />
                    </div>
                    <span className="text-3xl font-extrabold text-[#F29A0C]/30 tracking-tighter group-hover:text-[#FFD166] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {pillar.num}
                    </span>
                  </div>

                  <h3 className="text-lg sm:text-xl font-bold text-white relative z-10 leading-snug tracking-[0.02em] group-hover:text-[#FFD166] transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {pillar.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-white/80 leading-[1.7] relative z-10 font-normal tracking-[0.01em]">
                    {pillar.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>

        </div>

        {/* Bottom Glowing Border Shimmer */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{
            background: "linear-gradient(90deg, transparent, #FF8400, #FFD166, #F29A0C, transparent)",
          }}
        />
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          5. ACCREDITED LINEAGES (SOFT PALE OLIVE & SAFFRON GRADIENT BACKDROP + 3D TILT CARD)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-12 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(38, 56, 28, 0.85) 0%, rgba(62, 88, 48, 0.88) 30%, rgba(200, 105, 10, 0.82) 68%, rgba(242, 154, 12, 0.85) 100%)",
        }}
      >
        
        {/* Soft Floating Olive Green Aura Glow */}
        <motion.div
          animate={{
            scale: [1, 1.25, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 -left-16 w-[550px] h-[550px] rounded-full bg-[#7D9D64]/60 blur-[110px] pointer-events-none"
        />

        {/* Soft Floating Warm Saffron Aura Glow */}
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            x: [0, -30, 0],
            y: [0, 30, 0],
            opacity: [0.25, 0.55, 0.25],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-1/4 -right-16 w-[550px] h-[550px] rounded-full bg-[#F29A0C]/55 blur-[110px] pointer-events-none"
        />

        {/* Central Luminous Mesh Glow */}
        <motion.div
          animate={{
            scale: [1, 1.18, 1],
            opacity: [0.2, 0.45, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] h-[480px] rounded-full bg-gradient-to-r from-[#7D9D64]/45 via-[#F29A0C]/40 to-[#FF8400]/35 blur-[120px] pointer-events-none"
        />

        {/* Dynamic Multi-Directional Glowing Moving Particles Behind Card */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(28)].map((_, i) => {
            const particleColors = ["#FFD166", "#FFA41B", "#F29A0C", "#FFF3B0", "#9DC08B"];
            const color = particleColors[i % particleColors.length];
            const size = 3 + (i % 4) * 2.2;
            const xOffset = ((i * 17) % 60) - 30;
            const yOffset = -40 - ((i * 13) % 55);
            return (
              <motion.div
                key={i}
                animate={{
                  x: [0, xOffset, -xOffset * 0.6, 0],
                  y: [0, yOffset, yOffset * 0.5, 0],
                  opacity: [0.25, 0.9, 0.4, 0.9],
                  scale: [1, 1.5, 0.85, 1],
                }}
                transition={{
                  duration: 4.5 + (i % 6) * 1.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.22,
                }}
                className="absolute rounded-full"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  backgroundColor: color,
                  boxShadow: `0 0 ${size * 2}px ${color}`,
                  top: `${6 + (i * 7.5) % 86}%`,
                  left: `${3 + (i * 6.7) % 94}%`,
                }}
              />
            );
          })}
        </div>

        <div className="max-w-[1440px] mx-auto relative z-10">
          
          <LuxuryTiltCard maxTilt={5}>
            <div
              ref={bottomCardRef}
              onMouseMove={handleBottomCardMouseMove}
              className="p-8 sm:p-12 lg:p-16 rounded-[44px] text-white relative overflow-hidden shadow-[0_35px_100px_rgba(0,0,0,0.45)] border-2 border-[#F29A0C]/60 group backdrop-blur-3xl"
              style={{
                background: "linear-gradient(135deg, rgba(18, 8, 3, 0.92) 0%, rgba(37, 19, 7, 0.88) 50%, rgba(14, 5, 2, 0.94) 100%)",
              }}
            >
              
              {/* Background Photographic Sacred Mandala Artwork with High Opacity & Vivid Contrast */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-85 mix-blend-screen scale-[1.03] transition-transform duration-1000 group-hover:scale-108 pointer-events-none"
                style={{ backgroundImage: "url('/assets/trainers_bottom_bg.jpg')" }}
              />

              {/* Gradient Vignette to Protect Text Readability While Keeping Artwork Stunning */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/85 pointer-events-none" />

              {/* Dynamic Interactive Mouse Light Spotlight */}
              <div
                className="absolute pointer-events-none rounded-full blur-[70px] transition-opacity duration-300 opacity-90 group-hover:opacity-100"
                style={{
                  width: "500px",
                  height: "500px",
                  background: "radial-gradient(circle, rgba(242,154,12,0.45) 0%, rgba(255,209,102,0.18) 45%, transparent 70%)",
                  left: `${mousePos.x - 250}px`,
                  top: `${mousePos.y - 250}px`,
                }}
              />

              {/* Rotating Sacred Mandala Watermark in Corner Inside Card */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute -right-24 -bottom-24 w-[420px] h-[420px] opacity-25 text-[#FFD166] pointer-events-none"
              >
                <FaOm className="w-full h-full" />
              </motion.div>

              {/* Animated Golden Glowing Border Shimmer */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #F29A0C, transparent)",
                }}
              />

              <div className="relative z-10 space-y-12">
                
                <div className="text-center max-w-2xl mx-auto space-y-3">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/60 border border-[#F29A0C]/40 text-[#FFD166] text-xs font-bold uppercase tracking-widest shadow-md backdrop-blur-md">
                    <FaCertificate className="text-xs text-[#F29A0C]" />
                    <span>Accredited Lineages</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-[1.25] sm:leading-[1.2] tracking-[0.025em] sm:tracking-[0.03em] drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Certified by Premier Global Bodies
                  </h2>
                  <p className="text-white/90 text-sm sm:text-base font-medium leading-[1.65] tracking-[0.015em] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                    Our faculty holds prestigious credentials across the world's most revered yogic universities and international certification boards.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {LINEAGES.map((lineage, idx) => {
                    const Icon = lineage.icon;
                    return (
                      <motion.div
                        key={idx}
                        whileHover={{ y: -6, scale: 1.04 }}
                        transition={{ duration: 0.25 }}
                        className="p-5 rounded-2xl bg-black/50 border border-white/20 backdrop-blur-xl hover:bg-black/70 hover:border-[#F29A0C] transition-all duration-300 text-center space-y-2.5 group/item shadow-xl relative overflow-hidden"
                      >
                        <div className="w-12 h-12 mx-auto rounded-xl bg-[#F29A0C]/20 text-[#FFD166] border border-[#F29A0C]/30 flex items-center justify-center text-xl group-hover/item:scale-110 group-hover/item:bg-[#F29A0C] group-hover/item:text-white transition-all duration-300 shadow-md">
                          <Icon />
                        </div>
                        <h4 className="text-sm font-bold text-white leading-snug tracking-[0.02em]">{lineage.name}</h4>
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#F29A0C]/35 text-[#FFD166] text-[10px] font-bold border border-[#F29A0C]/50">
                          {lineage.badge}
                        </span>
                        <p className="text-[11px] text-white/85 leading-tight">
                          {lineage.subtitle}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Trust Footer */}
                <div className="pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/90">
                  <div className="flex items-center gap-2">
                    <FiCheckCircle className="text-[#FFD166] text-base flex-shrink-0" />
                    <span>All faculty members undergo rigorous annual alignment re-certification.</span>
                  </div>
                  <Link
                    to="/courses"
                    className="inline-flex items-center gap-2 text-[#FFD166] hover:text-white font-bold transition-colors"
                  >
                    <span>Explore Courses Taught by Masters</span>
                    <FiArrowRight />
                  </Link>
                </div>

              </div>

            </div>
          </LuxuryTiltCard>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════════
          6. FULL PROFILE & CREDENTIALS MODAL DRAWER (ZERO BOOKING FORMS)
      ══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {activeTrainerModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveTrainerModal(null)}
              className="fixed inset-0 bg-black/65 backdrop-blur-md"
            />

            {/* Modal Dialog Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-[36px] border border-[#F4D9B5] shadow-[0_30px_100px_rgba(0,0,0,0.35)] z-10 p-6 sm:p-10 text-[#111111]"
            >
              {/* Close Button */}
              <button
                onClick={() => setActiveTrainerModal(null)}
                className="absolute top-6 right-6 w-11 h-11 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] hover:border-[#F29A0C] flex items-center justify-center text-lg transition-all cursor-pointer z-20"
                aria-label="Close modal"
              >
                <FiX />
              </button>

              {/* Modal Content */}
              <div className="space-y-8">
                
                {/* Header Profile Section */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-2">
                  <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-3xl overflow-hidden border-2 border-[#F4D9B5] flex-shrink-0 shadow-md bg-[#FFF8EE]">
                    <img
                      src={activeTrainerModal.image}
                      alt={activeTrainerModal.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="space-y-2 text-center sm:text-left flex-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F29A0C]/10 text-[#F29A0C] text-xs font-bold">
                      <GiLotus />
                      <span>{activeTrainerModal.sealText}</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-[#111111] leading-tight tracking-[0.02em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {activeTrainerModal.name}
                    </h3>
                    <p className="text-sm font-semibold text-[#F29A0C]">
                      {activeTrainerModal.title}
                    </p>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 text-xs text-[#666666] pt-1">
                      <span className="flex items-center gap-1 font-medium">
                        <FiMapPin className="text-[#F29A0C]" /> {activeTrainerModal.location}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-[#111111]">
                        <FiStar className="text-[#F29A0C] fill-current" /> {activeTrainerModal.rating} ({activeTrainerModal.students} Learners)
                      </span>
                      <span className="flex items-center gap-1 font-medium">
                        <FiClock className="text-[#6B8A5A]" /> {activeTrainerModal.experience}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quote Highlight */}
                {activeTrainerModal.quote && (
                  <div className="p-5 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] text-center sm:text-left italic text-sm font-medium text-[#333333]">
                    "{activeTrainerModal.quote}"
                  </div>
                )}

                {/* Bio & Philosophy */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug tracking-[0.015em]">
                    <FiFeather className="text-[#F29A0C]" />
                    <span>Teaching Philosophy & Background</span>
                  </h4>
                  <p className="text-sm text-[#555555] leading-relaxed">
                    {activeTrainerModal.philosophy || activeTrainerModal.bio}
                  </p>
                  {activeTrainerModal.careerJourney && (
                    <p className="text-sm text-[#666666] leading-relaxed pt-1">
                      <strong className="text-[#222222]">Career Journey: </strong>
                      {activeTrainerModal.careerJourney}
                    </p>
                  )}
                </div>

                {/* Certifications List */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug tracking-[0.015em]">
                    <FaCertificate className="text-[#6B8A5A]" />
                    <span>Accreditations & Certifications</span>
                  </h4>
                  <div className="space-y-2">
                    {activeTrainerModal.certifications?.map((cert, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#FFF8EE]/60 border border-[#F4D9B5]/80 text-xs sm:text-sm text-[#222222]">
                        <FiCheckCircle className="text-[#6B8A5A] text-base flex-shrink-0 mt-0.5" />
                        <span>{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Offerings & Specialties */}
                <div className="space-y-3">
                  <h4 className="text-base font-bold text-[#111111] flex items-center gap-2 leading-snug tracking-[0.015em]">
                    <FiLayers className="text-[#F29A0C]" />
                    <span>Master Class Offerings</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeTrainerModal.offerings?.map((offering, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-white border border-[#F4D9B5] space-y-1 shadow-sm">
                        <div className="text-xs sm:text-sm font-bold text-[#111111]">{offering.title}</div>
                        <div className="text-xs text-[#666666]">{offering.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="pt-4 border-t border-[#F4D9B5] flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-[#777777]">
                    Part of the Divyyog Foundation Certified Master Faculty.
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setActiveTrainerModal(null)}
                      className="px-6 py-2.5 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-xs font-bold text-[#111111] hover:bg-[#F4D9B5]/40 transition-colors cursor-pointer"
                    >
                      Close Dossier
                    </button>
                    <Link
                      to="/courses"
                      onClick={() => setActiveTrainerModal(null)}
                      className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-xs font-bold text-white shadow-md hover:scale-105 transition-transform"
                    >
                      Browse Related Courses
                    </Link>
                  </div>
                </div>

              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
